import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Input, InputNumber, Select, Space, Table, Tag, Typography } from "antd";
import { toast } from "react-toastify";
import { request } from "../../functions/request";

const { Text, Title } = Typography;

/**
 * Lançamento da curva inteira de uma bolsa, colada do Excel.
 *
 * As telas de Fechamento de Bolsas gravam um vencimento por vez; com onze
 * vencimentos por bolsa, ninguém usava — a curva ficava só no Excel e as
 * matérias eram montadas à mão. Aqui a curva entra numa colagem, cai nas
 * mesmas tabelas e alimenta o site, o app e as matérias automáticas.
 */

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

const rotulo = (periodo) => {
  const [a, m] = periodo.split("-").map(Number);
  return `${MESES[m - 1]}/${String(a).slice(2)}`;
};

/** 'dez/26', 'DEZ 26', '12/2026', '01/12/2026' -> '2026-12' (ou null). */
export function periodoDoTexto(texto) {
  const t = String(texto || "").trim().toLowerCase();
  let m = t.match(/^([a-zç]{3})[a-zç]*[\s/.-]*(\d{2}|\d{4})$/);
  if (m) {
    const mes = MESES.indexOf(m[1]);
    if (mes === -1) return null;
    const ano = m[2].length === 2 ? 2000 + Number(m[2]) : Number(m[2]);
    return `${ano}-${String(mes + 1).padStart(2, "0")}`;
  }
  m = t.match(/^(?:\d{1,2}\/)?(\d{1,2})\/(\d{2}|\d{4})$/);
  if (m && Number(m[1]) >= 1 && Number(m[1]) <= 12) {
    const ano = m[2].length === 2 ? 2000 + Number(m[2]) : Number(m[2]);
    return `${ano}-${String(m[1]).padStart(2, "0")}`;
  }
  return null;
}

/** '1.234,56' | '352,80' | '352.80' -> número (ou null). */
export function numeroDoTexto(texto) {
  const t = String(texto || "").trim().replace(/\s/g, "");
  if (!/^-?[\d.,]+$/.test(t)) return null;
  const n = Number(t.includes(",") ? t.replace(/\./g, "").replace(",", ".") : t);
  return Number.isFinite(n) ? n : null;
}

/**
 * Lê o bloco colado: em cada linha, a primeira célula que for contrato e
 * o primeiro número depois dela (o preço do dia — as colunas seguintes da
 * planilha, sessão anterior e variação, são ignoradas). Cabeçalho, título
 * e observações não têm contrato e ficam de fora sozinhos.
 */
export function lerColagem(texto) {
  const lidas = [];
  for (const linha of String(texto || "").split(/\r?\n/)) {
    const celulas = linha.split(/\t|;|\s{2,}/).map((c) => c.trim()).filter(Boolean);
    const i = celulas.findIndex((c) => periodoDoTexto(c));
    if (i === -1) continue;
    const valor = celulas.slice(i + 1).map(numeroDoTexto).find((n) => n !== null && n > 0);
    if (valor === undefined) continue;
    lidas.push({ periodo: periodoDoTexto(celulas[i]), valor });
  }
  return lidas;
}

const br = (v, casas = 2) =>
  v === null || v === undefined ? "—" : Number(v).toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas });
const dataBr = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "—");

/** Acima disso a linha fica marcada: costuma ser vírgula no lugar errado. */
const ALERTA_VARIACAO = 5;

export default function LancamentoCurva() {
  const [produtos, setProdutos] = useState([]);
  const [apelido, setApelido] = useState(null);
  const [situacao, setSituacao] = useState(null);
  const [valores, setValores] = useState({});
  const [colagem, setColagem] = useState("");
  const [gravando, setGravando] = useState(false);

  useEffect(() => {
    request("/bolsa_curva/produtos", { method: "GET" })
      .then((lista) => {
        setProdutos(lista);
        if (lista.length) setApelido(lista[0].apelido);
      })
      .catch(() => toast.error("Não foi possível carregar as bolsas."));
  }, []);

  const carregar = useCallback((qual) => {
    if (!qual) return;
    request(`/bolsa_curva/${qual}`, { method: "GET" })
      .then((s) => {
        setSituacao(s);
        // O que já foi lançado hoje volta para a grade, para corrigir.
        const doDia = {};
        s.linhas.forEach((l) => { if (l.hoje !== null) doDia[l.periodo] = l.hoje; });
        setValores(doDia);
        setColagem("");
      })
      .catch(() => toast.error("Não foi possível carregar a curva."));
  }, []);

  useEffect(() => carregar(apelido), [apelido, carregar]);

  const aplicarColagem = (texto) => {
    setColagem(texto);
    const lidas = lerColagem(texto);
    if (!lidas.length) return;
    setValores((atual) => {
      const novo = { ...atual };
      lidas.forEach((l) => { novo[l.periodo] = l.valor; });
      return novo;
    });
    toast.success(`${lidas.length} contrato(s) lido(s) da colagem.`);
  };

  // Contratos da sessão anterior + os que vieram na colagem e são novos.
  const linhas = useMemo(() => {
    const anteriores = Object.fromEntries((situacao?.linhas || []).map((l) => [l.periodo, l.anterior]));
    const periodos = [...new Set([...Object.keys(anteriores), ...Object.keys(valores)])].sort();
    return periodos.map((periodo) => {
      const hoje = valores[periodo] ?? null;
      const anterior = anteriores[periodo] ?? null;
      const pct = hoje !== null && anterior ? ((hoje - anterior) / anterior) * 100 : null;
      return { periodo, contrato: rotulo(periodo), anterior, hoje, var: hoje !== null && anterior !== null ? hoje - anterior : null, pct };
    });
  }, [situacao, valores]);

  const preenchidas = linhas.filter((l) => l.hoje !== null);
  const suspeitas = preenchidas.filter((l) => l.pct !== null && Math.abs(l.pct) > ALERTA_VARIACAO);

  const gravar = async () => {
    setGravando(true);
    try {
      const r = await request(`/bolsa_curva/${apelido}`, {
        method: "POST",
        body: JSON.stringify({ linhas: preenchidas.map((l) => ({ periodo: l.periodo, valor: l.hoje })) }),
      });
      toast.success(`Curva gravada: ${r.gravados} contrato(s).`);
      setSituacao(r.situacao);
    } catch (erro) {
      toast.error(`Não gravou: ${String(erro.message || erro).replace(/^Error:\s*/, "")}`);
    } finally {
      setGravando(false);
    }
  };

  const unidade = situacao?.bolsa?.unidade || "";
  const colunas = [
    { title: "Contrato", dataIndex: "contrato", width: 90, render: (v) => <Text strong>{v}</Text> },
    { title: `Anterior (${dataBr(situacao?.diaAnterior)})`, dataIndex: "anterior", align: "right", render: (v) => br(v) },
    {
      title: `Hoje (${dataBr(situacao?.hoje)})`,
      dataIndex: "hoje",
      align: "right",
      render: (v, l) => (
        <InputNumber
          aria-label={`Hoje ${l.contrato}`}
          value={v}
          min={0}
          step={0.01}
          decimalSeparator=","
          precision={2}
          style={{ width: 130 }}
          onChange={(n) => setValores((atual) => {
            const novo = { ...atual };
            if (n === null || n === undefined) delete novo[l.periodo];
            else novo[l.periodo] = n;
            return novo;
          })}
        />
      ),
    },
    { title: "Var.", dataIndex: "var", align: "right", render: (v) => br(v) },
    {
      title: "%",
      dataIndex: "pct",
      align: "right",
      render: (v) =>
        v === null ? "—" : Math.abs(v) > ALERTA_VARIACAO ? <Tag color="orange">{br(v)}</Tag> : br(v),
    },
  ];

  const anteriorAntiga = situacao?.diaAnterior && situacao.hoje &&
    (new Date(situacao.hoje) - new Date(situacao.diaAnterior)) / 86400000 > 4;

  return (
    <Card style={{ margin: 16 }}>
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <Title level={4} style={{ margin: 0 }}>Lançar curva de bolsa</Title>

        <Space wrap>
          <Text>Bolsa:</Text>
          <Select
            style={{ minWidth: 280 }}
            value={apelido}
            onChange={setApelido}
            options={produtos.map((p) => ({ value: p.apelido, label: `${p.rotulo} (${p.unidade})` }))}
          />
          {situacao?.momento && <Tag color="green">Já lançada hoje às {situacao.momento.slice(11)}</Tag>}
        </Space>

        {anteriorAntiga && (
          <Alert
            type="warning"
            showIcon
            message={`A última curva gravada é de ${dataBr(situacao.diaAnterior)}.`}
            description="A variação de hoje será calculada contra ela. Se houve pregão depois disso, lance antes a curva desse dia ou confira a coluna %."
          />
        )}

        <Input.TextArea
          rows={4}
          value={colagem}
          placeholder={"Cole aqui as linhas da planilha (Ctrl+V). Ex.:\nout/26\t352,80\t357,10\t-4,30\t-1,20\ndez/26\t353,60\t356,90\t-3,30\t-0,92"}
          onChange={(e) => aplicarColagem(e.target.value)}
        />

        <Table
          size="small"
          rowKey="periodo"
          pagination={false}
          columns={colunas}
          dataSource={linhas}
          locale={{ emptyText: "Cole a curva acima." }}
        />

        {suspeitas.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message={`Variação acima de ${ALERTA_VARIACAO}% em ${suspeitas.map((l) => l.contrato).join(", ")}`}
            description="Confira se não é vírgula no lugar errado antes de gravar."
          />
        )}

        <Space>
          <Button type="primary" loading={gravando} disabled={!preenchidas.length} onClick={gravar}>
            Gravar curva ({preenchidas.length} contrato{preenchidas.length === 1 ? "" : "s"})
          </Button>
          <Text type="secondary">Valores em {unidade}. Contrato sem valor de hoje não é gravado.</Text>
        </Space>
      </Space>
    </Card>
  );
}
